import React, { useState, useEffect, useRef } from 'react';

// --- Starfield Animation Component ---
const StarfieldBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    // Star properties
    const numStars = 300;
    const stars = [];

    // Create stars
    for (let i = 0; i < numStars; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 1.5,  
        vx: (Math.random() - 0.5) * 0.2, // horizontal velocity
        vy: (Math.random() - 0.5) * 0.2, // vertical velocity
        alpha: Math.random(), // Initial opacity
        alphaChange: (Math.random() - 0.5) * 0.015 // How fast it fades in/out
      });
    }

    // Animation loop
    const animate = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw and update stars
      stars.forEach(star => {
        // Update position for movement
        star.x += star.vx;
        star.y += star.vy;

        // Wrap stars around the screen
        if (star.x < 0) star.x = canvas.width;
        if (star.x > canvas.width) star.x = 0;
        if (star.y < 0) star.y = canvas.height;
        if (star.y > canvas.height) star.y = 0;
        
        // Update alpha for twinkling effect
        star.alpha += star.alphaChange;
        
        // Reverse direction if alpha hits bounds
        if (star.alpha <= 0 || star.alpha >= 1) {
          star.alphaChange *= -1;
        }

        // Draw star
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(248, 255, 229, ${star.alpha})`; // Using the #F8FFE5 color with variable alpha
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup function
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, zIndex: 0, width: '100%', height: '100%' }} />;
};


// --- SVG ICONS ---
const MenuIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" x2="20" y1="12" y2="12" />
    <line x1="4" x2="20" y1="6" y2="6" />
    <line x1="4" x2="20" y1="18" y2="18" />
  </svg>
);

const XIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const InstagramIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 text-gray-400 group-hover:text-white transition-colors duration-300"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line></svg>
);

const YoutubeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 text-gray-400 group-hover:text-white transition-colors duration-300"><path d="M2.5 17a24.12 24.12 0 0 1 0-10A24.12 24.12 0 0 1 2.5 17V7c0-1.1.9-2 2-2h15a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2v-2.53z"></path><path d="m10 15 5-3-5-3z"></path></svg>
);

const LinkedinIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 text-gray-400 group-hover:text-white transition-colors duration-300"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect width="4" height="12" x="2" y="9"></rect><circle cx="4" cy="4" r="2"></circle></svg>
);

const ArrowRightIcon = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
);

const BrainCircuitIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 mb-4 text-[#06D6A0]"><path d="M12 2a10 10 0 0 0-10 10c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.9-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.9 1.52 2.34 1.08 2.91.83.1-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.1.39-1.99 1.03-2.69a3.6 3.6 0 0 1 .1-2.64s.84-.27 2.75 1.02a9.58 9.58 0 0 1 5 0c1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.4.1 2.64.64.7 1.03 1.6 1.03 2.69 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85v2.73c0 .27.16.59.67.5A10 10 0 0 0 22 12a10 10 0 0 0-10-10z"/></svg>
);

const CreativeSparkIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 mb-4 text-[#06D6A0]"><path d="m12 3-1.45 4.92L6 10l4.92 1.45L12 21l1.45-4.92L22 14l-4.92-1.45L12 3z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>
);

const ShieldCheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 mb-4 text-[#06D6A0]"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
);


// --- WEBSITE DATA ---
const websiteData = {
    navbar: {
        links: ["Home", "About Us", "Services", "Our Work", "Industries", "Contact"],
        ctaButton: "Let's Create Together"
    },
    footer: {
        contactInfo: {
            phone: ["+92 305 8664339", "+97150 605 1395"],
            email: "operations@shorstudios.com",
            address: "34-A, TIP Phase 3, Lahore, Punjab, Pakistan"
        },
        socialLinks: ["Instagram", "YouTube", "LinkedIn"],
        copyright: "© 2025 Shor Studios. All Rights Reserved."
    },
    home: {
        hero: {
            title: "SHOR STUDIOS",
            subtitle: "360 MARKETING SOLUTIONS FOR YOUR BRAND",
            cta: "Explore Our Work"
        },
        clients: {
            title: "Trusted By Industry Leaders",
            clientLogos: ["EMAAR", "DAMAC", "LAKE CITY", "HYUNDAI", "PEL", "Outfitters", "FARAZ MANAN", "ASIM JOFA", "PESHAWAR ZALMI"]
        },
        servicesOverview: [
            { title: "360° Marketing Solutions", description: "We craft holistic marketing strategies that cater to every aspect of your brand's journey, ensuring seamless integration across all platforms." },
            { title: "In-House Production Studio", description: "Our full-service production house is the heart of our creative storytelling, equipped with the latest technology and an experienced team." }
        ],
        featuredWork: {
            title: "Our Work",
            projects: [
                { title: "TBA Racing Endorsement", description: "Pakistan's trailblazer in endorsing TBA Racing with unparalleled 360° marketing solutions.", category: "Action Films & Commercials", image: "bestdrifter.jpeg" },
                { title: "Fashion & Lifestyle Campaigns", description: "Capturing brand essence through professional fashion campaigns and compelling narratives.", category: "Fashion Films", image: "fashion.png" },
                { title: "Brand Identity & Design", description: "Creating unique brand identities, from logos to stationery, that resonate with your audience.", category: "Branding", image: "branding.png" }
            ]
        },
        whyChooseUs: {
            title: "Why We're Your Best Choice",
            points: [
                { title: "Integrated Approach", description: "We handle everything under one roof, ensuring cohesive strategies and faster turn arounds." },
                { title: "Creativity Meets Data", description: "Our campaigns are driven by creativity, backed by data insights to maximize ROI." },
                { title: "Industry Expertise", description: "With years of experience across diverse industries, we understand what works best for your market." },
                { title: "Client-Centric", description: "Your success is our success. We collaborate closely with you to ensure your vision becomes a reality." }
            ]
        },
        aiSection: {
            title: "Powered by ShorOS: The Future of Media",
            subtitle: "We're not just an agency; we are a media and marketing technology company. Our proprietary AI platform, ShorOS, gives our clients a strategic advantage that traditional agencies cannot match.",
            points: [
                { icon: "BrainCircuit", title: "AI-Driven Strategy", description: "Our system analyzes market data to generate project scopes, predict campaign outcomes, and identify high-value opportunities for your brand in real-time." },
                { icon: "CreativeSpark", title: "Automated Creative Production", description: "ShorOS automates the creation of ad variations, storyboards, and initial post-production, delivering high-quality content at unprecedented speed and scale." },
                { icon: "ShieldCheck", title: "Intelligent Brand Guardianship", description: "A dedicated 'Brand Guardian' AI audits all output, from social posts to video commercials, ensuring every single asset is 100% on-brand before human review." }
            ]
        }
    },
    about: {
        whoWeAre: {
            title: "Who We Are",
            paragraph1: "Shor Studios is a full-service 360° marketing solutions provider committed to turning your brand's vision into a powerful reality.",
            paragraph2: "Our approach is built around understanding your unique brand identity and delivering tailored strategies that align with your goals.",
            paragraph3: "With a customer-centric philosophy at our core, we prioritize clear, transparent communication and a collaborative partnership, ensuring we serve as a trusted ally in driving your success within your industry."
        }
    },
    services: {
        marketing: {
            title: "360° Marketing Solutions",
            services: [
                { name: "Digital Marketing", details: "Performance-driven campaigns, social media management, SEO, PPC, and email marketing." },
                { name: "Brand Strategy & Development", details: "Creating unique brand identities, positioning strategies, and communication guidelines." },
                { name: "Content Marketing", details: "Captivating storytelling through blogs, videos, and social media content." },
                { name: "Event Marketing", details: "Designing and managing live and virtual events that amplify your brand presence." },
                { name: "Traditional Marketing", details: "TV commercials, radio ads, and print media campaigns with high-impact messaging." }
            ]
        },
        production: {
            title: "In-House Production Studio",
            services: [
                { name: "Video Production", details: "Corporate videos, commercials, short films, and brand stories." },
                { name: "Photography", details: "Professional product shoots, fashion campaigns, and event photography." },
                { name: "Design & Animation", details: "World-class graphic design, motion graphics, and 3D animation." },
                { name: "Post-Production", details: "Expert editing, color grading, sound design, and special effects." },
                { name: "UGC Creation", details: "User-Generated Content strategies that inspire authentic engagement." }
            ]
        }
    },
    work: {
        portfolio: {
            title: "Our Portfolio",
            description: "We've had the privilege of working with a diverse range of clients, from startups to multinational corporations.",
            filters: ["All", "TVC For Brands", "Social Media Designs", "Branding", "Website Designs", "Catalogue Layout"],
            projects: [
                { category: "TVC For Brands", image: "sonu.png", id: 1 },
                { category: "TVC For Brands", image: "film.png", id: 2 },
                { category: "TVC For Brands", image: "car.png", id: 3 },
                { category: "Social Media Designs", image: "destiny.png", id: 4 },
                { category: "Social Media Designs", image: "shell.png", id: 5 },
                { category: "Social Media Designs", image: "realestate.png", id: 6 },
                { category: "Branding", image: "clothing.png", id: 7 },
                { category: "Website Designs", image: "website.png", id: 8 },
                { category: "Catalogue Layout", image: "sifa.png", id: 9 },
            ]
        }
    },
    industries: {
        industriesServed: {
            title: "Industries We Serve",
            list: [ "Retail & E-Commerce", "Hospitality & Tourism", "Real Estate & Construction", "Technology & Startups", "Health & Wellness", "Fashion & Lifestyle"]
        }
    },
    contact: {
        form: {
            title: "Let's Create Together",
            subtitle: "Whether you're looking to elevate your brand's presence, launch a new product, or tell your story through stunning visuals, SHOR STUDIOS is here to make it happen.",
            fields: ["Full Name", "Email Address", "Phone Number", "Service of Interest", "Your Message"]
        },
        details: {
            phone: ["+92 305 8664339", "+971 50 605 1395"],
            email: "operations@shorstudios.com",
            address: "34-A, TIP Phase 3, Lahore, Punjab, Pakistan"
        }
    }
};

// --- Custom Hook for Scroll Animations (FIXED FOR WARNINGS) ---
const useScrollFadeIn = () => {
    const elementsRef = useRef([]);

    useEffect(() => {
        const currentElements = elementsRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('fade-in-visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1 }
        );

        currentElements.forEach((el) => {
            if (el) observer.observe(el);
        });

        return () => {
            currentElements.forEach((el) => {
                if (el) observer.unobserve(el);
            });
        };
    }, []);

    const addToRefs = (el) => {
        if (el && !elementsRef.current.includes(el)) {
            elementsRef.current.push(el);
        }
    };

    return addToRefs;
};


// --- GLOBAL COMPONENTS ---

const Navbar = ({ setCurrentPage, currentPage }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    
    const handleNavClick = (page) => {
        setCurrentPage(page);
        setIsOpen(false);
        window.scrollTo(0, 0);
    }

    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || isOpen ? 'bg-black/80 backdrop-blur-md' : 'bg-transparent'}`}>
            <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                <button onClick={() => handleNavClick('Home')} className="flex items-center">
                    <img src="/Slogo.png" alt="Shor Studios Logo" className="h-16 w-auto"/>
                </button>
                <div className="hidden md:flex items-center space-x-8">
                    {websiteData.navbar.links.map(link => (
                        <button key={link} onClick={() => handleNavClick(link.replace(' ', ''))} className={`text-sm font-medium transition-colors duration-300 ${currentPage === link.replace(' ', '') ? 'text-[#F8FFE5]' : 'text-gray-400 hover:text-[#F8FFE5]'}`}>{link}</button>
                    ))}
                </div>
                <div className="hidden md:block">
                     <button onClick={() => handleNavClick('Contact')} className="bg-[#F8FFE5] text-black text-sm font-semibold px-6 py-2 rounded-full hover:bg-[#F8FFE5]/90 transition-colors duration-300">{websiteData.navbar.ctaButton}</button>
                </div>
                <div className="md:hidden">
                    <button onClick={() => setIsOpen(!isOpen)} className="text-[#F8FFE5]">
                        {isOpen ? <XIcon className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
                    </button>
                </div>
            </div>
            {isOpen && (
                <div className="md:hidden bg-black/90 backdrop-blur-md">
                    <div className="flex flex-col items-center py-8 space-y-6">
                        {websiteData?.navbar?.links?.map(link => (
                            <button key={link} onClick={() => handleNavClick(link.replace(' ', ''))} className="text-lg text-gray-300 hover:text-white transition-colors duration-300">{link}</button>
                        ))}
                         <button onClick={() => handleNavClick('Contact')} className="bg-white text-black text-sm font-semibold px-8 py-3 rounded-full hover:bg-gray-200 transition-colors duration-300 mt-4">{websiteData.navbar.ctaButton}</button>
                    </div>
                </div>
            )}
        </nav>
    );
};

const Footer = ({ setCurrentPage }) => (
    <footer className="bg-black border-t border-gray-900 text-gray-400">
        <div className="container mx-auto px-6 py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                <div className="col-span-1 lg:col-span-2">
                    {/* --- LOGO UPDATED HERE --- */}
                    <img src="/Slogo.png" alt="Shor Studios Logo" className="h-16 w-auto mb-4"/>
                    <p className="max-w-md">A 360° marketing and production agency dedicated to creating noise that resonates.</p>
                </div>
                <div>
                    <h3 className="text-[#F8FFE5] font-semibold tracking-wider">Navigation</h3>
                    <ul className="mt-4 space-y-2">
                        {websiteData?.navbar?.links?.map(link => (
                            <li key={link}><button onClick={() => {setCurrentPage(link.replace(' ', '')); window.scrollTo(0,0)}} className="hover:text-[#F8FFE5] transition-colors duration-300">{link}</button></li>
                        ))}
                    </ul>
                </div>
                <div>
                    <h3 className="text-[#F8FFE5] font-semibold tracking-wider">Contact Us</h3>
                    <div className="mt-4 space-y-2">
                        <p>{websiteData.footer.contactInfo.phone.join(' / ')}</p>
                        <p>{websiteData.footer.contactInfo.email}</p>
                        <p>{websiteData.footer.contactInfo.address}</p>
                    </div>
                </div>
            </div>
            <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
                <p className="text-sm text-center md:text-left">{websiteData.footer.copyright}</p>
                <div className="flex space-x-4 mt-4 md:mt-0">
                    {/* FIXED for accessibility warning */}
                    <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="group"><InstagramIcon /></a>
                    <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="group"><YoutubeIcon /></a>
                    <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="group"><LinkedinIcon /></a>
                </div>
            </div>
        </div>
    </footer>
);

// --- HOME PAGE COMPONENTS ---

const HeroSection = ({ setCurrentPage }) => (
    <section className="h-screen bg-black flex items-center justify-center text-[#F8FFE5] relative overflow-hidden">
        <StarfieldBackground />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black"></div>
        <div className="text-center z-10 p-6">
            <h1 className="text-6xl md:text-9xl font-normal tracking-wider uppercase title-reveal">{websiteData.home.hero.title}</h1>
            <p className="mt-4 text-lg md:text-2xl font-light tracking-wider text-gray-300 subtitle-reveal">{websiteData.home.hero.subtitle}</p>
            <button onClick={() => {setCurrentPage('OurWork'); window.scrollTo(0,0)}} className="mt-12 bg-[#F8FFE5] text-black font-semibold px-10 py-4 rounded-full hover:bg-[#F8FFE5]/90 transition-all duration-300 transform hover:scale-105 cta-reveal">{websiteData.home.hero.cta}</button>
        </div>
    </section>
);

const ClientsMarquee = () => (
    <div className="bg-black py-12 md:py-20 overflow-hidden">
        <div className="text-center mb-10">
            <h3 className="text-sm uppercase tracking-widest text-gray-400">{websiteData.home.clients.title}</h3>
        </div>
        <div className="relative w-full">
            <div className="marquee">
                <div className="marquee-content">
                    {websiteData.home.clients.clientLogos.map((client, index) => (
                        <span key={index} className="text-2xl md:text-4xl font-bold text-gray-600 mx-8 md:mx-12">{client}</span>
                    ))}
                    {/* Duplicate for seamless loop */}
                    {websiteData.home.clients.clientLogos.map((client, index) => (
                        <span key={`dup-${index}`} className="text-2xl md:text-4xl font-bold text-gray-600 mx-8 md:mx-12">{client}</span>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

const ServicesOverview = () => {
    const addToRefs = useScrollFadeIn();
    return (
        <section className="bg-black text-[#F8FFE5] py-20 md:py-32">
            <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
                {websiteData.home.servicesOverview.map((service, index) => (
                    <div key={index} ref={addToRefs} className="fade-in">
                        <h2 className="text-3xl md:text-4xl font-bold">{service.title}</h2>
                        <p className="mt-4 text-gray-400 leading-relaxed">{service.description}</p>
                        <button className="mt-6 group text-[#06D6A0] font-semibold flex items-center">
                            Learn More
                            <ArrowRightIcon className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </div>
                ))}
            </div>
        </section>
    );
};

const FeaturedWork = ({ setCurrentPage }) => {
    const addToRefs = useScrollFadeIn();
    const isUrl = (str) => /\.(jpeg|jpg|gif|png|webp)$/.test(str);

    return (
        <section className="bg-black text-[#F8FFE5] py-20 md:py-32">
            <div className="container mx-auto px-6">
                <div ref={addToRefs} className="fade-in text-center mb-12 md:mb-20">
                    <h2 className="text-4xl md:text-5xl font-bold">{websiteData.home.featuredWork.title}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {websiteData.home.featuredWork.projects.map((project, index) => (
                        <div key={index} ref={addToRefs} className="fade-in group overflow-hidden rounded-lg" style={{transitionDelay: `${index * 100}ms`}} onClick={() => {setCurrentPage('OurWork'); window.scrollTo(0,0)}}>
                            <div className="relative h-full">
                                <img 
                                    src={isUrl(project.image) ? `/${project.image}` : `https://placehold.co/600x400/111827/ffffff?text=${encodeURIComponent(project.image)}`} 
                                    alt={project.title} 
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent"></div>
                                <div className="absolute bottom-0 left-0 p-6">
                                    <span className="text-xs uppercase tracking-widest text-gray-300">{project.category}</span>
                                    <h3 className="text-xl font-bold text-[#F8FFE5] mt-1">{project.title}</h3>
                                    <p className="text-gray-400 mt-2 text-sm">{project.description}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const WhyChooseUs = () => {
    const addToRefs = useScrollFadeIn();
    return (
        <section className="bg-gray-900/50 text-[#F8FFE5] py-20 md:py-32">
            <div className="container mx-auto px-6">
                <div ref={addToRefs} className="fade-in text-center mb-12 md:mb-20">
                    <h2 className="text-4xl md:text-5xl font-bold">{websiteData.home.whyChooseUs.title}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    {websiteData.home.whyChooseUs.points.map((point, index) => (
                        <div key={index} ref={addToRefs} className="fade-in bg-black p-8 rounded-lg border border-gray-800 hover:border-[#06D6A0]/80 transition-all duration-300" style={{transitionDelay: `${index * 100}ms`}}>
                            <h3 className="text-xl font-bold">{point.title}</h3>
                            <p className="mt-3 text-gray-400">{point.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const AiPoweredSection = () => {
    const addToRefs = useScrollFadeIn();
    const icons = {
        BrainCircuit: <BrainCircuitIcon />,
        CreativeSpark: <CreativeSparkIcon />,
        ShieldCheck: <ShieldCheckIcon />
    };

    return (
        <section className="bg-black text-[#F8FFE5] py-20 md:py-32">
            <div className="container mx-auto px-6">
                <div ref={addToRefs} className="fade-in text-center mb-12 md:mb-20 max-w-3xl mx-auto">
                    <h2 className="text-4xl md:text-5xl font-bold">{websiteData.home.aiSection.title}</h2>
                    <p className="mt-4 text-gray-400">{websiteData.home.aiSection.subtitle}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {websiteData.home.aiSection.points.map((point, index) => (
                        <div key={index} ref={addToRefs} className="fade-in bg-gray-900/50 p-8 rounded-lg border border-gray-800 hover:border-[#06D6A0]/50 transition-colors duration-300" style={{transitionDelay: `${index * 100}ms`}}>
                            {icons[point.icon]}
                            <h3 className="text-xl font-bold text-[#F8FFE5]">{point.title}</h3>
                            <p className="mt-3 text-gray-400">{point.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};


// --- ABOUT US PAGE COMPONENTS ---

const WhoWeAre = () => {
    const addToRefs = useScrollFadeIn();
    return(
        <section className="bg-black text-[#F8FFE5] pt-32 pb-20 md:pt-48 md:pb-32">
            <div className="container mx-auto px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 ref={addToRefs} className="fade-in text-4xl md:text-6xl font-bold">{websiteData.about.whoWeAre.title}</h1>
                    <div ref={addToRefs} className="fade-in mt-12 space-y-6 text-gray-300 text-lg leading-relaxed" style={{transitionDelay: `100ms`}}>
                        <p>{websiteData.about.whoWeAre.paragraph1}</p>
                        <p>{websiteData.about.whoWeAre.paragraph2}</p>
                        <p>{websiteData.about.whoWeAre.paragraph3}</p>
                    </div>
                </div>
            </div>
        </section>
    );
};

// --- SERVICES PAGE COMPONENTS ---

const ServiceSection = ({ title, services, image }) => {
    const addToRefs = useScrollFadeIn();
    return (
        <section className="bg-black text-[#F8FFE5] py-20 md:py-24">
            <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <div className="lg:order-2">
                    <h2 ref={addToRefs} className="fade-in text-4xl md:text-5xl font-bold">{title}</h2>
                    <div className="mt-8 space-y-6">
                        {services.map((service, index) => (
                            <div key={index} ref={addToRefs} className="fade-in border-b border-gray-800 pb-4" style={{transitionDelay: `${(index + 1) * 100}ms`}}>
                                <h3 className="text-xl font-semibold text-[#F8FFE5]">{service.name}</h3>
                                <p className="mt-2 text-gray-400">{service.details}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <div ref={addToRefs} className="fade-in lg:order-1 h-80 md:h-[500px] rounded-lg overflow-hidden">
                     <img src={`/${image}`} alt={title} className="w-full h-full object-cover"/>
                </div>
            </div>
        </section>
    );
};

const ServicesPage = () => (
    <div className="pt-24 md:pt-32 bg-black">
        <ServiceSection 
            title={websiteData.services.marketing.title} 
            services={websiteData.services.marketing.services} 
            image="strategic.png" 
        />
        <ServiceSection 
            title={websiteData.services.production.title} 
            services={websiteData.services.production.services} 
            image="production.png" 
        />
    </div>
);


// --- OUR WORK PAGE COMPONENTS ---

const PortfolioGrid = () => {
    const [activeFilter, setActiveFilter] = useState('All');
    const addToRefs = useScrollFadeIn();
    const isUrl = (str) => /\.(jpeg|jpg|gif|png|webp)$/.test(str);

    const filteredProjects = activeFilter === 'All'
        ? websiteData.work.portfolio.projects
        : websiteData.work.portfolio.projects.filter(p => p.category === activeFilter);
    
    return(
        <section className="bg-black text-[#F8FFE5] pt-32 pb-20 md:pt-48 md:pb-32">
            <div className="container mx-auto px-6">
                <div ref={addToRefs} className="fade-in max-w-3xl mx-auto text-center">
                    <h1 className="text-4xl md:text-6xl font-bold">{websiteData.work.portfolio.title}</h1>
                    <p className="mt-4 text-lg text-gray-400">{websiteData.work.portfolio.description}</p>
                </div>

                <div ref={addToRefs} className="fade-in flex flex-wrap justify-center gap-2 md:gap-4 my-12 md:my-16">
                    {websiteData.work.portfolio.filters.map(filter => (
                        <button 
                            key={filter} 
                            onClick={() => setActiveFilter(filter)}
                            className={`px-4 py-2 text-sm md:text-base rounded-full border transition-all duration-300 ${activeFilter === filter ? 'bg-[#06D6A0] text-black border-[#06D6A0]' : 'border-gray-700 text-gray-300 hover:border-[#06D6A0]/80 hover:text-[#F8FFE5]'}`}
                        >
                            {filter}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {filteredProjects.map((project, index) => (
                         <div key={project.id} ref={addToRefs} className="fade-in group relative overflow-hidden rounded-lg aspect-w-4 aspect-h-3">
                            <img 
                                src={isUrl(project.image) ? `/${project.image}` : `https://placehold.co/600x450/111827/ffffff?text=${encodeURIComponent(project.image)}`} 
                                alt={project.image} 
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-4">
                                <p className="text-[#F8FFE5] text-center font-semibold">{project.category}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// --- INDUSTRIES PAGE COMPONENTS ---

const IndustriesServed = () => {
    const addToRefs = useScrollFadeIn();
    return(
        <section className="bg-black text-[#F8FFE5] pt-32 pb-20 md:pt-48 md:pb-32">
            <div className="container mx-auto px-6">
                <div ref={addToRefs} className="fade-in text-center mb-12 md:mb-20">
                    <h1 className="text-4xl md:text-6xl font-bold">{websiteData.industries.industriesServed.title}</h1>
                </div>
                <div className="max-w-4xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                        {websiteData.industries.industriesServed.list.map((industry, index) => (
                            <div key={index} ref={addToRefs} className="fade-in border-b border-gray-800 py-6" style={{transitionDelay: `${index * 100}ms`}}>
                                <p className="text-xl md:text-2xl font-semibold text-gray-200">{industry}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// --- CONTACT PAGE COMPONENTS ---

const ContactSection = () => {
    const addToRefs = useScrollFadeIn();
    return(
        <section className="bg-black text-[#F8FFE5] pt-32 pb-20 md:pt-48 md:pb-32">
            <div className="container mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20">
                    <div ref={addToRefs} className="fade-in">
                        <h1 className="text-4xl md:text-5xl font-bold">{websiteData.contact.form.title}</h1>
                        <p className="mt-4 text-gray-400">{websiteData.contact.form.subtitle}</p>
                        
                        {/* --- EDITED FORM --- */}
                        <form 
                            action="https://formspree.io/f/YOUR_UNIQUE_ID" // <-- YOU WILL REPLACE THIS URL
                            method="POST"
                            className="mt-8 space-y-4"
                        >
                            <input key="Full Name" type="text" name="name" placeholder="Full Name" className="w-full bg-gray-900/50 border border-gray-700 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-[#06D6A0]/50 transition-all"/>
                            <input key="Email Address" type="email" name="email" placeholder="Email Address" className="w-full bg-gray-900/50 border border-gray-700 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-[#06D6A0]/50 transition-all"/>
                            <input key="Phone Number" type="tel" name="phone" placeholder="Phone Number" className="w-full bg-gray-900/50 border border-gray-700 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-[#06D6A0]/50 transition-all"/>
                            <input key="Service of Interest" type="text" name="service_interest" placeholder="Service of Interest" className="w-full bg-gray-900/50 border border-gray-700 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-[#06D6A0]/50 transition-all"/>
                            <textarea key="Your Message" name="message" placeholder="Your Message" rows="4" className="w-full bg-gray-900/50 border border-gray-700 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-[#06D6A0]/50 transition-all"></textarea>
                            <button type="submit" className="w-full bg-[#06D6A0] text-black font-semibold p-3 rounded-md hover:bg-[#06D6A0]/90 transition-colors duration-300">Send Message</button>
                        </form>
                        {/* --- END EDITED FORM --- */}

                    </div>
                    <div ref={addToRefs} className="fade-in" style={{transitionDelay: '200ms'}}>
                        <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-8 h-full">
                            <h3 className="text-2xl font-bold text-[#F8FFE5]">Contact Details</h3>
                             <div className="mt-6 space-y-4 text-gray-300">
                                <div>
                                    <h4 className="font-semibold text-[#F8FFE5]">Phone</h4>
                                    <p>{websiteData.contact.details.phone[0]}</p>
                                    <p>{websiteData.contact.details.phone[1]}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-[#F8FFE5]">Email</h4>
                                    <p>{websiteData.contact.details.email}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-[#F8FFE5]">Address</h4>
                                    <p>{websiteData.contact.details.address}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// --- PAGE COMPONENTS ---

const HomePage = ({ setCurrentPage }) => (
    <>
        <HeroSection setCurrentPage={setCurrentPage}/>
        <ClientsMarquee />
        <ServicesOverview />
        <FeaturedWork setCurrentPage={setCurrentPage}/>
        <WhyChooseUs />
        <AiPoweredSection /> 
    </>
);

const AboutUsPage = () => (
    <WhoWeAre />
);

const IndustriesPage = () => (
    <IndustriesServed />
);

const ContactPage = () => (
    <ContactSection />
);

const OurWorkPage = () => (
    <PortfolioGrid />
);


// --- MAIN APP COMPONENT ---

export default function App() {
    const [currentPage, setCurrentPage] = useState('Home');

    const renderPage = () => {
        switch (currentPage) {
            case 'Home':
                return <HomePage setCurrentPage={setCurrentPage}/>;
            case 'AboutUs':
                return <AboutUsPage />;
            case 'Services':
                return <ServicesPage />;
            case 'OurWork':
                return <OurWorkPage />;
            case 'Industries':
                return <IndustriesPage />;
            case 'Contact':
                return <ContactPage />;
            default:
                return <HomePage setCurrentPage={setCurrentPage}/>;
        }
    };
    
    // Injecting CSS styles directly for animations
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            body {
                background-color: #000;
                color: #F8FFE5;
                font-family: 'Montserrat', sans-serif;
            }
            
            h1, h2, h3, .logo-font {
                font-family: 'Bebas Neue', sans-serif;
            }

            .title-reveal { animation: reveal 1s ease-out forwards; }
            .subtitle-reveal { animation: reveal 1s ease-out 0.2s forwards; opacity: 0; }
            .cta-reveal { animation: reveal 1s ease-out 0.4s forwards; opacity: 0; }

            @keyframes reveal {
                0% { transform: translateY(30px); opacity: 0; }
                100% { transform: translateY(0); opacity: 1; }
            }

            .marquee {
                width: 100%;
                overflow: hidden;
            }

            .marquee-content {
                display: flex;
                animation: marquee 40s linear infinite;
                will-change: transform;
            }

            @keyframes marquee {
                0% { transform: translateX(0%); }
                100% { transform: translateX(-50%); }
            }
            
            .fade-in {
                opacity: 0;
                transform: translateY(20px);
                transition: opacity 0.6s ease-out, transform 0.6s ease-out;
            }

            .fade-in-visible {
                opacity: 1;
                transform: translateY(0);
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    return (
        <div className="bg-black">
            <Navbar setCurrentPage={setCurrentPage} currentPage={currentPage} />
            <main>
                {renderPage()}
            </main>
            <Footer setCurrentPage={setCurrentPage}/>
        </div>
    );
}


